import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {setCurrentBatchDetails} from '../../slices/batchDetailsSlice'
import {RootState} from '../../store'
import {
  CancelBatchRequest,
  CancelBatchResponse,
  CompletedBatchesRequest,
  CompletedBatchesResponse,
  CreateBatchRequest,
  CreateBatchResponse,
  DownloadTemplateRequest,
  PendingListBatchRequest,
  PendingListBatchResponse,
  ScheduleBatchRequest,
  ScheduleBatchResponse,
  UpdatePendingBatchResponse,
  ScheduleBatchesRequest,
  ScheduleBatchesResponse,
  UpdateBatchRequest,
  UpdateBatchResponse,
  DetailBatchResponse,
  DetailBatchRequest,
  ErrorDetailBatchResponse,
  ErrorDetailBatchRequest,
  DownloadBatchRequest,
  ErrorBatchesResponse,
  ErrorBatchesRequest,
  DownloadBatchResponse,
} from '../interfaces/batch'
export const URL = `${process.env.REACT_APP_API_URL}/payments/v1/batches/`

export const batchApi = createApi({
  reducerPath: 'batchApi',
  baseQuery: fetchBaseQuery({
    baseUrl: URL,
    //TODO: Investigar si existe forma de implementar el token de autorizacion en middlewares de redux
    prepareHeaders: (headers, {getState}) => {
      const token = (getState() as RootState).authSlice.session_id
      headers.set('Authorization', `Bearer ${token}`)
      return headers
    },
  }),
  tagTypes: ['Batch', 'Scheduled', 'PendingList'],
  endpoints: (builder) => ({
    getPendingListBatch: builder.query<PendingListBatchResponse, PendingListBatchRequest>({
      query: (data) => ({
        url: `unscheduled`,
        method: 'Post',
        body: data,
      }),
      providesTags: ['PendingList'],
    }),
    createBatch: builder.mutation<CreateBatchResponse, CreateBatchRequest>({
      query: ({file, body}) => {
        const formData = new FormData()
        formData.append('file', file, file.name)
        formData.append('payload', JSON.stringify(body))

        return {
          url: `create/unscheduled`,
          method: 'Post',
          body: formData,
          providesTags: ['Batch'],
        }
      },
      async onQueryStarted(arg, {dispatch, queryFulfilled}) {
        const {data} = await queryFulfilled
        dispatch(setCurrentBatchDetails(data.responseContent))
      },
    }),
    updatePendingBatch: builder.mutation<UpdatePendingBatchResponse, UpdatePendingBatchResponse>({
      query: (data) => ({
        url: `pending/update`,
        method: 'POST',
        body: data,
      }),
    }),
    scheduleBatch: builder.mutation<ScheduleBatchResponse, ScheduleBatchRequest>({
      query: (data) => ({
        url: `schedule`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PendingList'],
    }),
    downloadTemplate: builder.query<string, DownloadTemplateRequest>({
      query: (requestData) => ({
        url: `template`,
        method: 'POST',
        responseType: 'arrayBuffer',
        body: requestData,
        responseHandler: async (response) => {
          const buffer = await response.arrayBuffer()
          const uint8Array = new Uint8Array(buffer)
          const serializedArray = JSON.stringify(Array.from(uint8Array))
          return serializedArray
        },
      }),
    }),
    getScheduleBatches: builder.query<ScheduleBatchesResponse, ScheduleBatchesRequest>({
      query: (requestData) => ({
        url: `scheduled`,
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['Scheduled'],
      serializeQueryArgs: ({endpointName}) => endpointName,
      transformResponse(response: ScheduleBatchesResponse, meta, arg) {
        return {...response, page: arg.requestContent?.page}
      },
      merge: (currentCacheData, responseData) => {
        if (responseData.page && responseData.page > 1) {
          currentCacheData.responseContent.push(...responseData.responseContent)
          return currentCacheData
        }
        return responseData
      },
      forceRefetch({currentArg, previousArg}) {
        return currentArg?.requestContent !== previousArg?.requestContent
      },
    }),
    getErrorBatches: builder.query<ErrorBatchesResponse, ErrorBatchesRequest>({
      query: (requestData) => ({
        url: `error`,
        method: 'POST',
        body: requestData,
      }),
      serializeQueryArgs: ({endpointName}) => endpointName,
      transformResponse(response: ErrorBatchesResponse, meta, arg) {
        return {...response, page: arg.requestContent?.page}
      },
      merge: (currentCacheData, responseData) => {
        if (responseData.page && responseData.page > 1) {
          currentCacheData.responseContent.push(...responseData.responseContent)
          return currentCacheData
        }
        return responseData
      },
      forceRefetch({currentArg, previousArg}) {
        return currentArg?.requestContent !== previousArg?.requestContent
      },
    }),
    getCompletedBatches: builder.query<CompletedBatchesResponse, CompletedBatchesRequest>({
      query: (requestData) => ({
        url: `completed`,
        method: 'POST',
        body: requestData,
      }),
      serializeQueryArgs: ({endpointName}) => endpointName,
      transformResponse(response: CompletedBatchesResponse, meta, arg) {
        return {...response, page: arg.requestContent?.page, filter: arg.requestContent?.filter}
      },
      merge: (currentCacheData, responseData) => {
        if (
          responseData.page &&
          responseData.filter &&
          responseData.page > 1 &&
          responseData.filter.searchTerm === currentCacheData.filter?.searchTerm &&
          responseData.filter.startDate === currentCacheData.filter?.startDate &&
          responseData.filter.endDate === currentCacheData.filter?.endDate
        ) {
          currentCacheData.responseContent.push(...responseData.responseContent)
          return currentCacheData
        }
        return responseData
      },
      forceRefetch({currentArg, previousArg}) {
        return currentArg?.requestContent !== previousArg?.requestContent
      },
    }),
    getDetailBatch: builder.query<DetailBatchResponse, DetailBatchRequest>({
      query: (requestData) => ({
        url: 'details/pending',
        method: 'POST',
        body: requestData,
      }),
    }),
    getDetailCompletedBatch: builder.query<DetailBatchResponse, DetailBatchRequest>({
      query: (requestData) => ({
        url: 'details/completed',
        method: 'POST',
        body: requestData,
      }),
    }),
    getErrorDetailBatch: builder.query<ErrorDetailBatchResponse, ErrorDetailBatchRequest>({
      query: (requestData) => ({
        url: 'details/errors',
        method: 'POST',
        body: requestData,
      }),
    }),
    updateBatch: builder.mutation<UpdateBatchResponse, UpdateBatchRequest>({
      query: (requestData) => ({
        url: 'scheduled/update',
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['Scheduled'],
    }),
    cancelBatch: builder.mutation<CancelBatchResponse, CancelBatchRequest>({
      query: (requestData) => ({
        url: 'delete',
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['Scheduled'],
    }),
    downloadBatch: builder.mutation<DownloadBatchResponse, DownloadBatchRequest>({
      query: (requestData) => ({
        url: `completed/download`,
        method: 'POST',
        responseType: 'arrayBuffer',
        body: requestData,
        responseHandler: async (response) => {
          const buffer = await response.arrayBuffer()
          const uint8Array = new Uint8Array(buffer)
          const serializedArray = JSON.stringify(Array.from(uint8Array))
          return {
            filename: response?.headers?.get('Content-Disposition')?.split('filename=')[1],
            file: serializedArray,
          }
        },
      }),
    }),
  }),
})

export const {
  useCreateBatchMutation,
  useDownloadTemplateQuery,
  useGetScheduleBatchesQuery,
  useGetErrorBatchesQuery,
  useGetCompletedBatchesQuery,
  useUpdateBatchMutation,
  useCancelBatchMutation,
  useGetDetailBatchQuery,
  useGetDetailCompletedBatchQuery,
  useGetErrorDetailBatchQuery,
  useGetPendingListBatchQuery,
  useScheduleBatchMutation,
  useDownloadBatchMutation,
} = batchApi
